"use client";

/**
 * PlatformToggle Component
 *
 * Animated toggle between Instagram and TikTok platforms.
 * Used in setup flow and ConnectAccountModal for consistent UI.
 *
 * Features:
 * - Spring animation for active indicator
 * - Colored Instagram logo (color when active, grayscale when inactive)
 * - TikTok logo in black circle
 */
import { c as _c } from "react/compiler-runtime";
import { motion } from "framer-motion";
import Image from "next/image";
import { FaTiktok } from "react-icons/fa";
type Platform = "instagram" | "tiktok";
interface PlatformToggleProps {
  platform: Platform;
  onPlatformChange: (platform: Platform) => void;
  /** Optional: disable the toggle */
  disabled?: boolean;
  /** Optional: make toggle full width */
  fullWidth?: boolean;
}
export default function PlatformToggle(t0) {
  const $ = _c(24);
  const {
    platform,
    onPlatformChange,
    disabled: t1,
    fullWidth: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const fullWidth = t2 === undefined ? false : t2;
  const t3 = `${fullWidth ? "flex w-full" : "inline-flex"} rounded-xl bg-[#f5f5f5] p-1`;
  const t4 = platform === "instagram";
  let t5;
  if ($[0] !== disabled || $[1] !== onPlatformChange) {
    t5 = () => !disabled && onPlatformChange("instagram");
    $[0] = disabled;
    $[1] = onPlatformChange;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const t6 = `transition-[opacity,filter] duration-200 ${platform === "instagram" ? "opacity-100 saturate-100" : "opacity-60 saturate-0"}`;
  let t7;
  if ($[3] !== t6) {
    t7 = <Image src="/img/icons/instagram.svg" width={18} height={18} alt="Instagram" className={t6} />;
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== disabled || $[6] !== fullWidth || $[7] !== t4 || $[8] !== t5 || $[9] !== t7) {
    t8 = <PlatformButton isActive={t4} onClick={t5} disabled={disabled} fullWidth={fullWidth} icon={t7} label="Instagram" />;
    $[5] = disabled;
    $[6] = fullWidth;
    $[7] = t4;
    $[8] = t5;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = platform === "tiktok";
  let t10;
  if ($[11] !== disabled || $[12] !== onPlatformChange) {
    t10 = () => !disabled && onPlatformChange("tiktok");
    $[11] = disabled;
    $[12] = onPlatformChange;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-black"><FaTiktok className="text-[10px] text-white" /></div>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== disabled || $[16] !== fullWidth || $[17] !== t10 || $[18] !== t9) {
    t12 = <PlatformButton isActive={t9} onClick={t10} disabled={disabled} fullWidth={fullWidth} icon={t11} label="TikTok" />;
    $[15] = disabled;
    $[16] = fullWidth;
    $[17] = t10;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t3 || $[22] !== t8) {
    t13 = <div className={t3}>{t8}{t12}</div>;
    $[20] = t12;
    $[21] = t3;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}

// Platform toggle button component
interface PlatformButtonProps {
  isActive: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  fullWidth?: boolean;
}
function PlatformButton(t0) {
  const $ = _c(11);
  const {
    isActive,
    onClick,
    icon,
    label,
    disabled,
    fullWidth
  } = t0;
  const t1 = `relative flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-[color,opacity] duration-200 ${fullWidth ? "flex-1" : ""} ${isActive ? "text-ink" : "text-black/40 hover:text-black/60"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`;
  let t2;
  if ($[0] !== isActive) {
    t2 = isActive && <motion.div layoutId="platform-indicator" className="absolute inset-0 rounded-lg bg-white shadow-sm" transition={{
      type: "spring",
      stiffness: 400,
      damping: 30
    }} />;
    $[0] = isActive;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon || $[3] !== label) {
    t3 = <span className="relative z-10 flex items-center gap-2">{icon}{label}</span>;
    $[2] = icon;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== disabled || $[6] !== onClick || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <button type="button" onClick={onClick} disabled={disabled} className={t1}>{t2}{t3}</button>;
    $[5] = disabled;
    $[6] = onClick;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}